/**
 * Somewhere for the back gesture to go. Ported from PropPay LX (lib/back-guard.ts).
 *
 * An installed iOS app has no back button, no URL bar and no tab strip: the
 * edge swipe is the ONLY way back. At the bottom of the history stack WebKit
 * reads that swipe as "leave this document" — and for an app launched from the
 * Home Screen there is no document behind it, so what arrives is a blank white
 * screen with no chrome and no way out but force-quitting. It is easiest to hit
 * on the first screen of a fresh launch, where the reader is most likely to
 * swipe out of habit, and it looks exactly like the app has crashed.
 *
 * The fix: one entry this module owns sits under the whole session, pointing
 * at the app's root. Backing into it lands on SaleFish rather than nowhere, and
 * it re-arms itself, so the swipe after that has somewhere to go too. Ordinary
 * Back — the page you actually came from — is untouched.
 *
 * Deliberately iOS-and-installed only:
 *   - In a BROWSER TAB, back past the beginning means "the site I came from".
 *     That is correct, and an entry of ours would steal it.
 *   - On ANDROID it means "close the app" — the platform's own contract.
 *
 * None of this is visible in a desktop browser, the Simulator's Safari, or
 * Safari on the same phone: all of them have chrome and a document behind
 * them. It only appears once the app is on the Home Screen — so it is
 * unverified until someone swipes back from the first screen of the installed
 * app on a real device.
 */
import { displayMode, isIOS } from "@/lib/pwa";

/**
 * The state object that marks the one entry this module owns. SaleFish's own
 * code does write state objects (`{}` from several replaceState calls,
 * `{ fromList: true }` from SectionNav), so the test is for THIS key, never
 * for "any state at all".
 */
const SENTINEL = { salefishBackGuard: true };

function isSentinel(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { salefishBackGuard?: unknown }).salefishBackGuard === true
  );
}

/** Where back-past-the-beginning lands: the root, which routes a signed-in reader home and anyone else to sign-in. */
export const HOME_PATH = "/";

/** A fresh navigation, a reload, or a step through the back/forward stack. */
function navigationType(): string {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") return "navigate";
  const [nav] = performance.getEntriesByType("navigation");
  return (nav as PerformanceNavigationTiming | undefined)?.type ?? "navigate";
}

/**
 * Put one entry underneath the session, and keep it there. Call from main.tsx
 * BEFORE React mounts: the seeding moves the URL twice and no router should
 * see the middle of it, and the `popstate` listener registered here must run
 * ahead of wouter's.
 *
 * Returns whether it installed (for the test; the app ignores it).
 */
export function installBackGuard(env?: {
  ios?: boolean;
  standalone?: boolean;
  history?: History;
  location?: Location;
  addEventListener?: (type: "popstate", fn: () => void) => void;
  navType?: string;
}): boolean {
  if (typeof window === "undefined" && !env?.history) return false;
  const ios = env?.ios ?? isIOS();
  const standalone = env?.standalone ?? displayMode() === "standalone";
  if (!ios || !standalone) return false;

  const history = env?.history ?? window.history;
  const location = env?.location ?? window.location;
  const listen = env?.addEventListener ?? ((type, fn) => window.addEventListener(type, fn));

  /*
    A fresh entry on top of the stop we are standing on, so the swipe AFTER
    this one lands here again rather than off the end. A push truncates the
    forward stack — the trade for never landing on the blank screen.
  */
  const rearm = () => history.pushState(null, "", HOME_PATH);

  if (isSentinel(history.state)) {
    // Standing ON the stop in a document that has only just started: the
    // reader swiped back onto it and iOS rebuilt the app rather than resuming
    // it, so the popstate below never ran. Re-arm, or the next swipe falls out.
    rearm();
  } else if ((env?.navType ?? navigationType()) === "navigate") {
    /*
      A brand-new entry — a launch from the Home Screen, a `location.assign`,
      or iOS restarting the app at start_url. Give it a stop of its own.

      Not `reload` (the service worker taking a new build) and not
      `back_forward`: those entries were already guarded when they were made,
      and seeding again would bury a second stop BETWEEN two real pages.
    */
    // Read first — replaceState moves the URL out from under location.href.
    const landedAt = location.href;
    history.replaceState(SENTINEL, "", HOME_PATH);
    history.pushState(null, "", landedAt);
  }

  listen("popstate", () => {
    // Out of history: the URL is already the root, so the router is about to
    // render it. All this has to do is put the next stop in place.
    if (isSentinel(history.state)) rearm();
  });
  return true;
}
